import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      className={`prose prose-sm dark:prose-invert max-w-none ${className}`}
      components={{
        h1: ({ node, ...props }) => (
          <h1 className="text-3xl font-bold mt-6 mb-4 text-gray-900 dark:text-white" {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h2 className="text-2xl font-bold mt-5 mb-3 text-gray-900 dark:text-white" {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h3 className="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white" {...props} />
        ),
        h4: ({ node, ...props }) => (
          <h4 className="text-lg font-bold mt-3 mb-2 text-gray-900 dark:text-white" {...props} />
        ),
        h5: ({ node, ...props }) => (
          <h5 className="text-base font-bold mt-2 mb-1 text-gray-900 dark:text-white" {...props} />
        ),
        h6: ({ node, ...props }) => (
          <h6 className="text-sm font-bold mt-2 mb-1 text-gray-900 dark:text-white" {...props} />
        ),
        p: ({ node, ...props }) => (
          <p className="text-base leading-relaxed text-gray-700 dark:text-gray-300 mb-4" {...props} />
        ),
        ul: ({ node, ...props }) => (
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2" {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol className="list-decimal list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2" {...props} />
        ),
        li: ({ node, ...props }) => (
          <li className="ml-4 text-gray-700 dark:text-gray-300" {...props} />
        ),
        blockquote: ({ node, ...props }) => (
          <blockquote
            className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300 mb-4"
            {...props}
          />
        ),
        code: ({ node, inline, ...props }: any) =>
          inline ? (
            <code
              className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono text-gray-900 dark:text-gray-100"
              {...props}
            />
          ) : (
            <code
              className="block bg-gray-200 dark:bg-gray-700 p-4 rounded text-sm font-mono text-gray-900 dark:text-gray-100 overflow-x-auto mb-4"
              {...props}
            />
          ),
        pre: ({ node, ...props }) => (
          <pre
            className="bg-gray-200 dark:bg-gray-700 p-4 rounded text-sm font-mono text-gray-900 dark:text-gray-100 overflow-x-auto mb-4"
            {...props}
          />
        ),
        table: ({ node, ...props }) => (
          <table
            className="w-full border-collapse border border-gray-300 dark:border-gray-600 mb-4"
            {...props}
          />
        ),
        thead: ({ node, ...props }) => (
          <thead className="bg-gray-200 dark:bg-gray-700" {...props} />
        ),
        tbody: ({ node, ...props }) => (
          <tbody className="divide-y divide-gray-300 dark:divide-gray-600" {...props} />
        ),
        tr: ({ node, ...props }) => (
          <tr className="border-b border-gray-300 dark:border-gray-600" {...props} />
        ),
        td: ({ node, ...props }) => (
          <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-700 dark:text-gray-300" {...props} />
        ),
        th: ({ node, ...props }) => (
          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-white font-bold text-left" {...props} />
        ),
        a: ({ node, ...props }) => (
          <a
            className="text-info-600 dark:text-info-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          />
        ),
        img: ({ node, ...props }) => (
          <img
            className="max-w-full h-auto rounded-lg mb-4"
            {...props}
          />
        ),
        hr: ({ node, ...props }) => (
          <hr className="border-t border-gray-300 dark:border-gray-600 my-6" {...props} />
        )
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
